/**
 * MiniChart: small interactive chart for the documentation pages.
 *
 * Renders a real lightweight-charts instance over a static session fixture
 * (frontend/src/docs/fixtures/*.json, exported by scripts/export_docs_fixtures.py)
 * and mounts the production indicator plugins with injected data. No API,
 * no SSE, no auth. What the docs show is exactly what the app renders.
 *
 * A crosshair tooltip shows the hovered candle (OHLC / volume / delta) plus
 * full details for any level under the cursor (absorption zones,
 * aggressive-order levels, VWAP values).
 *
 * Props:
 *   fixture   - fixture name, e.g. "2026-06-09" (required)
 *   plugins   - array of: 'absorption' | 'focus'
 *               | 'structure' | 'structure-detail' (need {fixture}-structure.json;
 *                 -detail also shows the tier-1 swing dots)
 *               | 'vwap' | 'vwap-split' | 'vwap-bands'
 *               | 'oi-dist' (needs {fixture}-greeks.json)
 *               | 'spx-quote' | 'spy-quote' (need {fixture}-quotes.json)
 *               | 'exposure-profile' (needs {fixture}-exposure.json)
 *   height    - chart height in px (default 360)
 *   highlight - array of prices to mark with dashed price lines
 *   range     - optional [fromTime, toTime] (epoch seconds) initial visible window
 *   caption   - optional caption rendered under the chart
 */

import { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries } from 'lightweight-charts';
import { OrderFlowPlugin } from '../indicators/OrderFlowPlugin';
import { VWAPIndicator } from '../indicators/VWAPIndicator';
import { GreeksHeatmapPlugin } from '../indicators/GreeksHeatmapPlugin';
import { UnderlyingQuoteIndicator } from '../indicators/UnderlyingQuoteIndicator';

const FIXTURES = import.meta.glob('./fixtures/*.json');

const CHART_OPTIONS = {
  layout: {
    background: { color: '#0f1419' },
    textColor: '#8a9ab0',
    fontSize: 11,
  },
  grid: {
    vertLines: { color: 'rgba(42, 51, 68, 0.5)' },
    horzLines: { color: 'rgba(42, 51, 68, 0.5)' },
  },
  timeScale: {
    timeVisible: true,
    secondsVisible: false,
    borderColor: '#2a3344',
  },
  rightPriceScale: { borderColor: '#2a3344' },
  crosshair: { mode: 0 },
};

const ET_TIME = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false,
});

function fmtTime(epochSec) {
  return ET_TIME.format(new Date(epochSec * 1000)) + ' ET';
}

function fmtVol(v) {
  if (v == null) return '-';
  return v >= 1000 ? `${(v / 1000).toFixed(1)}K` : String(v);
}

/** Compact signed magnitude for exposure greeks (B/M/K suffixes). */
function fmtExposure(v) {
  if (v == null) return '-';
  const sign = v < 0 ? '-' : '+';
  const a = Math.abs(v);
  if (a >= 1e9) return `${sign}${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${sign}${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${sign}${(a / 1e3).toFixed(0)}K`;
  return `${sign}${a.toFixed(1)}`;
}

/** Map fixture VWAP points to the shape VWAPIndicator._drawSet reads. */
function toVwapPoints(points) {
  return points.map(p => ({
    time: p.time,
    vwap: p.vwap,
    std_dev_1: p.sd1u,
    std_dev_1_lower: p.sd1l,
    std_dev_2: p.sd2u,
    std_dev_2_lower: p.sd2l,
  }));
}

/**
 * Build tooltip HTML for the hovered candle + everything under the cursor.
 * Each element pairs its data with an educational explainer: what the
 * mechanism is and what behavior to watch for, written from the element's
 * own state, so a broken level teaches something different from a defended one.
 */
function buildTooltipHtml({ time, candle, price, zones, focusLevels, vwapAt, oiBar,
                            ruler, quoteRows, expLevel }) {
  const row = (label, value, color = '#b0bcc8') =>
    `<div style="display:flex;justify-content:space-between;gap:14px;">
       <span style="color:#6b7a90;">${label}</span><span style="color:${color};">${value}</span>
     </div>`;
  const divider = '<div style="border-top:1px solid #2a3344;margin:6px 0;"></div>';
  const header = (text, color) =>
    `<div style="color:${color};font-weight:600;margin-bottom:3px;">${text}</div>`;
  const explain = (text) =>
    `<div style="color:#8a96a8;font-size:11px;line-height:1.5;margin-top:5px;font-style:italic;">${text}</div>`;

  // Candle data is a fallback: only shown when nothing more specific (a level,
  // VWAP) is under the cursor; a level hover should read as that level's story.
  const hasSpecific = zones.length > 0 || focusLevels.length > 0
    || (vwapAt != null && (vwapAt.eth != null || vwapAt.rth != null))
    || oiBar != null || expLevel != null;

  let html = `<div style="color:#e0e0e0;font-weight:600;margin-bottom:3px;">${fmtTime(time)}</div>`;

  // Three-currency ruler: the same arithmetic the production tooltip uses.
  // Cursor values are the anchor-inverse of the ES crosshair price. The
  // strike cells key off the hovered DISPLAYED strike: its ES-space
  // position (expLevel.price, strike x anchor tick-rounded) is divided
  // back into each row's currency with the signed cursor distance. The
  // live-ES cell is continuous: the cursor level re-mapped through the
  // row's own live-vs-anchor ratio (where ES trades when that instrument
  // is at the hovered level). Drift compares the hovered bucket's live
  // ES-to-underlying ratio against the T-1 anchor.
  if (ruler && price != null) {
    const strikeEs = expLevel?.price ?? null;
    const strikeCell = (v, anchor, decimals) => {
      if (strikeEs == null) return '';
      const sv = strikeEs / anchor;
      const dist = v - sv;
      return `${sv.toFixed(decimals)} (${dist >= 0 ? '+' : ''}${dist.toFixed(decimals)})`;
    };
    const rulerRow = (u, color, decimals) => {
      const anchor = ruler.anchors?.[u];
      if (anchor == null) return '';
      const v = price / anchor;
      const q = quoteRows?.[u];
      const liveEs = q?.drift != null
        ? `<span style="color:#8a96a8;">ES </span>${(price * (1 + q.drift / 1e4)).toFixed(2)}` : '';
      const drift = q?.drift != null ? `${q.drift >= 0 ? '+' : ''}${q.drift.toFixed(1)}bp` : '';
      return `<span style="color:${color};">${u.toUpperCase()}</span>` +
        `<span style="text-align:right;">${v.toFixed(decimals)}</span>` +
        `<span style="text-align:right;">${strikeCell(v, anchor, decimals)}</span>` +
        `<span style="text-align:right;">${liveEs}</span>` +
        `<span style="text-align:right;">${drift}</span>`;
    };
    html += `<div style="display:grid;grid-template-columns:auto auto auto auto auto;gap:1px 10px;` +
      `justify-content:start;font-variant-numeric:tabular-nums;margin-bottom:3px;">` +
      `<span style="color:#d1d5db;">ES</span>` +
      `<span style="text-align:right;">${price.toFixed(2)}</span>` +
      `<span style="text-align:right;">${strikeCell(price, 1, 2)}</span>` +
      `<span></span><span></span>` +
      rulerRow('spx', '#e0b040', 1) +
      rulerRow('spy', '#40c4aa', 2) +
      `</div>`;
    if (quoteRows) {
      for (const [u, q] of Object.entries(quoteRows)) {
        const color = u === 'spx' ? '#e0b040' : '#40c4aa';
        const decimals = u === 'spx' ? 1 : 2;
        html += row(`${u.toUpperCase()} bucket close`, q.native.toFixed(decimals), color);
        html += row('Rebased to ES', (q.native * q.anchor).toFixed(2));
      }
      html += explain(
        `The hollow candle is the real index price multiplied by the day's anchor ` +
        `(the prior session's close ratio). Because the SAME anchor places the option ` +
        `strike levels, the gap between the hollow candle and the ES candle IS the ` +
        `intraday drift of the ES-to-index relationship, shown in basis points in the ` +
        `last column.`);
    }
  }
  if (candle && !hasSpecific) {
    const dir = candle.close >= candle.open ? '#22c55e' : '#ef4444';
    html += row('O / H / L / C',
      `${candle.open} / ${candle.high} / ${candle.low} / <b style="color:${dir}">${candle.close}</b>`);
    html += row('Volume', fmtVol(candle.volume));
    // Fixtures carry the wire candle shape (buy_volume, no delta field);
    // derive delta = buys - sells = 2*buy_volume - volume.
    const delta = candle.buy_volume != null ? 2 * candle.buy_volume - candle.volume : null;
    if (delta != null) {
      html += row('Delta', `${delta > 0 ? '+' : ''}${delta}`,
        delta >= 0 ? '#22c55e' : '#ef4444');
      if (Math.abs(delta) > 0) {
        const side = delta > 0 ? 'buyers' : 'sellers';
        const moved = delta > 0 ? candle.close > candle.open : candle.close < candle.open;
        html += explain(
          `Delta = market buys - market sells: aggressive ${side} dominated this candle` +
          (moved ? ' and price followed them.'
                 : `, yet price didn't follow. One-sided aggression that fails to move price means passive orders on the other side are absorbing it.`));
      }
    }
  }

  // Absorption color says WHO (buyers blue, sellers orange). `side` is the defenders: buy =
  // buyers defended (support), sell = sellers defended (resistance). Defensive fallback maps the
  // old `direction` field (bid_absorbed -> buy, ask_absorbed -> sell) so fixtures exported before
  // the backend cutover still render these docs examples. The fallback drops once fixtures
  // regenerate against the new backend.
  const ACTIVITY_WORD = { inside_value: 'inside value', initiative: 'initiative', responsive: 'responsive' };
  for (const z of zones) {
    const side = z.side ?? (z.direction === 'bid_absorbed' ? 'buy' : z.direction === 'ask_absorbed' ? 'sell' : null);
    const isBuyers = side === 'buy';
    const color = isBuyers ? '#0080ff' : '#ff6000';
    const who = isBuyers ? 'buyers defended' : 'sellers defended';
    const center = z.center ?? z.price;
    const lower = z.lower ?? z.price_low;
    const upper = z.upper ?? z.price_high;
    const word = ACTIVITY_WORD[z.activity];
    html += divider;
    html += header(`Absorption: ${who}${center != null ? ` @ ${center.toFixed(2)}` : ''}`, color);
    if (lower != null && upper != null) html += row('Band', `${lower.toFixed(2)} - ${upper.toFixed(2)}`);
    if (z.absorbed_volume != null) html += row('Absorbed', fmtVol(z.absorbed_volume));
    if (z.wick_candles != null) html += row('Wick candles', String(z.wick_candles));
    const strength = z.stable_strength ?? z.strength;
    if (strength != null) html += row('Stable strength', strength.toFixed(2));
    if (word) html += row('Activity', word);
    if (z.state) {
      const stateColor = z.state === 'broken' ? '#ff6000'
        : z.state === 'provisional' ? '#b0bcc8' : color;
      html += row('State', z.state, stateColor);
    }
    const reads = Array.isArray(z.entry_reads) ? z.entry_reads : [];
    if (reads.length) {
      const last = reads[reads.length - 1];
      if (last.kind) html += row('Entry read', `${last.kind}${last.resolution ? ` (${last.resolution})` : ''}`);
    }
    if (z.session_date) html += row('Formed', z.session_date);
    html += explain(
      `Market orders traded into this band's wicks and were absorbed by resting orders on the other ` +
      `side (${isBuyers ? 'resting buyers took the selling: a support band' : 'resting sellers took the buying: a resistance band'}). ` +
      `It marks where passive orders were observed. ${z.state === 'broken'
        ? 'Price has since closed through the far edge by the break threshold; the band is shown to the present at half fill and is still watched.'
        : 'On a return, the entry read describes which side\'s market orders dominated the approach and the entry.'}`);
  }

  // Aggressive-order levels: color says WHO (buyers blue, sellers orange). Broken levels are not
  // served/not shown. strength is in whole contracts.
  for (const f of focusLevels) {
    if (f.state === 'broken') continue;
    const isBuyers = f.side === 'buy';
    const color = isBuyers ? '#0080ff' : '#ff6000';
    const who = isBuyers ? 'buyers aggressive' : 'sellers aggressive';
    const word = ACTIVITY_WORD[f.activity];
    html += divider;
    html += header(`Aggressive: ${who} @ ${f.price.toFixed(2)}`, color);
    if (f.big_order_vol != null) html += row('Big-order volume', fmtVol(f.big_order_vol));
    if (f.imbalance_vol != null) html += row('Imbalance volume', fmtVol(f.imbalance_vol));
    if (f.strength != null) html += row('Strength', fmtVol(Math.round(f.strength)));
    if (word) html += row('Activity', word);
    if (f.state) html += row('State', f.state, '#b0bcc8');
    if (f.session_date) html += row('Formed', f.session_date);
    html += explain(
      `A big single print of 50 or more contracts landed here and folded into a stacked same-side ` +
      `diagonal imbalance on its own volume candle: ${isBuyers ? 'buyers lifting offers' : 'sellers hitting bids'} ` +
      `with urgency. It marks where aggressive orders were observed, at the whole point.`);
  }

  if (oiBar) {
    const totalOi = (oiBar.call_oi || 0) + (oiBar.put_oi || 0);
    const skew = oiBar.call_oi > oiBar.put_oi ? 'call-heavy' : 'put-heavy';
    html += divider;
    html += header(`Open interest · strike ${oiBar.price.toFixed(0)}`, '#a855f7');
    html += row('Calls open', fmtVol(oiBar.call_oi));
    html += row('Puts open', fmtVol(oiBar.put_oi));
    html += explain(
      `${fmtVol(totalOi)} contracts were open at this strike at last night's settlement (${skew}). ` +
      `The bar lengths show the relative dealer exposure those standing positions imply: GEX, DEX, ` +
      `VEX and CEX stacked per strike. Unlike the intraday flow layers, none of this moves with spot: ` +
      `it's the positioning structure the session opened into, and it only changes overnight.`);
  }

  if (expLevel) {
    html += divider;
    html += header(`Exposure profile · strike ${expLevel.price.toFixed(2)}`, '#3b82f6');
    html += row('GEX (gamma)', fmtExposure(expLevel.gex), '#3b82f6');
    html += row('DEX (delta)', fmtExposure(expLevel.dex), '#ef4444');
    html += row('VEX (vanna)', fmtExposure(expLevel.vex), '#a855f7');
    html += row('CEX (charm)', fmtExposure(expLevel.cex), '#06b6d4');
    if (expLevel.colTime != null) html += row('As of', fmtTime(expLevel.colTime));
    html += explain(
      `Dealer-weighted exposure at this strike, evaluated at the price and remaining ` +
      `option life of the hovered window. Move the crosshair left to scrub history: ` +
      `each column is the profile as it stood at that time. A long gamma bar marks a ` +
      `strike where dealer hedging reacts most to the next point of movement, in ` +
      `either direction. Descriptive mechanics, not a direction call.`);
  }

  if (vwapAt && (vwapAt.eth != null || vwapAt.rth != null)) {
    html += divider;
    if (vwapAt.eth != null) html += row('VWAP (full session)', vwapAt.eth.toFixed(2), '#4a9eff');
    if (vwapAt.rth != null) html += row('VWAP (RTH)', vwapAt.rth.toFixed(2), '#f59e0b');
    if (price != null && vwapAt.eth != null) {
      const diff = price - vwapAt.eth;
      html += row('Cursor vs VWAP', `${diff > 0 ? '+' : ''}${diff.toFixed(2)} pts`,
        diff >= 0 ? '#22c55e' : '#ef4444');
      html += explain(
        `VWAP is the average price actually paid this session. ${diff >= 0
          ? 'Price here is above it, so the average buyer is in profit and pullbacks to VWAP test whether they defend their average'
          : 'Price here is below it, so the average buyer is underwater and rallies into VWAP meet trapped longs looking to exit at break-even'}. ` +
        `Institutions benchmark executions against VWAP, which concentrates orders around it.`);
    }
  }

  return html;
}

export default function MiniChart({
  fixture,
  plugins = [],
  height = 360,
  highlight = [],
  range = null,
  caption = null,
}) {
  const containerRef = useRef(null);
  const tooltipRef = useRef(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Load fixture JSON lazily (code-split per fixture)
  useEffect(() => {
    const loader = FIXTURES[`./fixtures/${fixture}.json`];
    if (!loader) {
      setError(`Unknown fixture: ${fixture}`);
      return;
    }
    let cancelled = false;
    loader().then(
      (mod) => { if (!cancelled) setData(mod.default || mod); },
      () => { if (!cancelled) setError(`Failed to load fixture: ${fixture}`); },
    );
    return () => { cancelled = true; };
  }, [fixture]);

  useEffect(() => {
    if (!data || !containerRef.current) return;

    const chart = createChart(containerRef.current, {
      ...CHART_OPTIONS,
      width: containerRef.current.clientWidth,
      height,
    });

    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#22c55e',
      downColor: '#ef4444',
      borderUpColor: '#22c55e',
      borderDownColor: '#ef4444',
      wickUpColor: '#22c55e',
      wickDownColor: '#ef4444',
    });
    series.setData(data.candles);

    const candlesByTime = new Map(data.candles.map(c => [c.time, c]));

    let orderflowPlugin = null;
    let vwapIndicator = null;

    const wantAbsorption = plugins.includes('absorption');
    const wantFocus = plugins.includes('focus');
    if (wantAbsorption || wantFocus) {
      orderflowPlugin = new OrderFlowPlugin(chart, series);
      series.attachPrimitive(orderflowPlugin);
      orderflowPlugin.updateCandles(data.candles);
      if (wantAbsorption) orderflowPlugin.setStaticData('absorption', data.absorption);
      if (wantFocus) orderflowPlugin.setStaticData('focusLevels', data.focusLevels);
    }

    // 'structure' / 'structure-detail' = market-structure overlay (tiered swing labels,
    // BOS/CHoCH break lines, bias chips) over {date}-structure.json; '-detail' also
    // shows the tier-1 swing dots (the layer's granular-detail toggle).
    let structureCancelled = false;
    const wantStructure = plugins.includes('structure') || plugins.includes('structure-detail');
    const structureLoader = wantStructure
      ? FIXTURES[`./fixtures/${fixture}-structure.json`] : null;
    if (structureLoader) {
      structureLoader().then((mod) => {
        if (structureCancelled) return;
        const payload = mod.default || mod;
        if (!orderflowPlugin) {
          orderflowPlugin = new OrderFlowPlugin(chart, series);
          series.attachPrimitive(orderflowPlugin);
          orderflowPlugin.updateCandles(data.candles);
        }
        if (plugins.includes('structure-detail')) orderflowPlugin.setStructShowDetail(true);
        orderflowPlugin.setStaticData('marketStructure', payload.marketStructure || []);
      });
    }

    const wantVwap = plugins.some(p => p.startsWith('vwap'));
    const vwapByTime = { eth: new Map(), rth: new Map() };
    if (wantVwap && data.vwap) {
      vwapIndicator = new VWAPIndicator(chart, series);
      vwapIndicator.enabled = true;
      vwapIndicator.sessionMode = plugins.includes('vwap-split') ? 'split' : 'full';
      vwapIndicator.showBand1 = plugins.includes('vwap-bands');
      vwapIndicator.showBand2 = false;
      vwapIndicator.sessionData = {
        eth: { vwap_data: toVwapPoints(data.vwap.eth || []) },
        rth: { vwap_data: toVwapPoints(data.vwap.rth || []) },
      };
      vwapIndicator.draw();
      for (const session of ['eth', 'rth']) {
        for (const p of data.vwap[session] || []) vwapByTime[session].set(p.time, p.vwap);
      }
    }

    // Options layers:
    //   'oi-dist'  = raw call/put open-interest distribution
    // This payload is heavy, so it lives in its own fixture file
    // ({date}-greeks.json, shape {oiBars}) loaded only here.
    let greeksPlugin = null;
    let greeksCancelled = false;
    let oiBarsForTooltip = null;
    const oiDist = plugins.includes('oi-dist');
    const greeksLoader = oiDist
      ? FIXTURES[`./fixtures/${fixture}-greeks.json`] : null;
    if (greeksLoader) {
      greeksLoader().then((mod) => {
        if (greeksCancelled) return;
        const payload = mod.default || mod;
        greeksPlugin = new GreeksHeatmapPlugin(chart, series, {
          timeframe: data.timeframe || '5m',
          showOiDistribution: oiDist,
          oiDistToggles: { callOi: true, putOi: true, net: true },
        });
        greeksPlugin.setStaticData({ oiBars: oiDist ? payload.oiBars : null });
        if (oiDist && payload.oiBars?.oi_bars) {
          const session = Object.values(payload.oiBars.oi_bars)[0];
          oiBarsForTooltip = session?.bars || null;
        }
      });
    }

    // 'exposure-profile' = per-strike exposure rows (4 diverging greek bars
    // per strike, crosshair scrubs history). Heavy payload, own fixture file
    // ({date}-exposure.json, shape = /api/greeks/exposure-rows response).
    let exposurePlugin = null;
    let exposureCancelled = false;
    let exposureRowsForTooltip = null;
    const exposureLoader = plugins.includes('exposure-profile')
      ? FIXTURES[`./fixtures/${fixture}-exposure.json`] : null;
    if (exposureLoader) {
      exposureLoader().then((mod) => {
        if (exposureCancelled) return;
        const payload = mod.default || mod;
        exposurePlugin = new GreeksHeatmapPlugin(chart, series, {
          timeframe: payload.meta?.tf || '30m',
          showExposureProfile: true,
          expProfileToggles: { gex: true, dex: true, vex: true, cex: true },
          exposureProfileMode: 'dealer',
        });
        exposurePlugin.setStaticData({ exposure: payload });
        const session = Object.values(payload.exposure_rows || {})[0];
        exposureRowsForTooltip = session?.candles || null;
      });
    }

    // 'spx-quote' / 'spy-quote' = real index prices as hollow candles rebased
    // onto the ES scale by the day's T-1 anchor (the production
    // UnderlyingQuoteIndicator over static segments, no SSE). Bars + anchors
    // live in {date}-quotes.json.
    const quoteIndicators = {};
    let quotesCancelled = false;
    let quoteAnchors = null;
    const wantQuotes = ['spx', 'spy'].filter(u => plugins.includes(`${u}-quote`));
    const quotesLoader = wantQuotes.length
      ? FIXTURES[`./fixtures/${fixture}-quotes.json`] : null;
    if (quotesLoader) {
      quotesLoader().then((mod) => {
        if (quotesCancelled) return;
        const payload = mod.default || mod;
        quoteAnchors = payload.anchors?.[payload.date] || null;
        for (const u of wantQuotes) {
          const bars = payload.quotes?.[payload.date]?.[u] || [];
          const anchor = quoteAnchors?.[u];
          if (!bars.length || anchor == null) continue;
          const q = new UnderlyingQuoteIndicator(chart, u);
          q.setTimeframe(data.timeframe || '5m');
          q.enable([{ date: payload.date, bars, anchor }], { noLive: true });
          quoteIndicators[u] = q;
        }
      });
    }

    const priceLines = highlight.map(price => series.createPriceLine({
      price,
      color: '#4a9eff',
      lineWidth: 1,
      lineStyle: 2,
      axisLabelVisible: true,
    }));

    if (range && range.length === 2) {
      chart.timeScale().setVisibleRange({ from: range[0], to: range[1] });
    } else {
      chart.timeScale().fitContent();
    }

    // ── Crosshair tooltip ────────────────────────────────────────────────
    const tooltip = tooltipRef.current;
    const showSplitVwap = plugins.includes('vwap-split');
    const onCrosshairMove = (param) => {
      if (!tooltip) return;
      if (!param.point || param.time == null) {
        tooltip.style.display = 'none';
        return;
      }
      const price = series.coordinateToPrice(param.point.y);
      const candle = candlesByTime.get(param.time);

      const zones = wantAbsorption && price != null
        ? (data.absorption || []).filter(z =>
            price >= (z.lower ?? z.price_low ?? z.center ?? z.price) - 0.25
            && price <= (z.upper ?? z.price_high ?? z.center ?? z.price) + 0.25)
        : [];
      const focusLevels = wantFocus && price != null
        ? (data.focusLevels || []).filter(l => Math.abs(l.price - price) <= 1.0)
        : [];
      const vwapAt = wantVwap ? {
        eth: vwapByTime.eth.get(param.time) ?? null,
        rth: showSplitVwap ? (vwapByTime.rth.get(param.time) ?? null) : null,
      } : null;
      let oiBar = null;
      if (oiBarsForTooltip && price != null) {
        let best = null;
        for (const b of oiBarsForTooltip) {
          const d = Math.abs(b.price - price);
          if (d <= 2.5 && (!best || d < Math.abs(best.price - price))) best = b;
        }
        oiBar = best;
      }

      // Quote overlays: ruler anchors + the hovered bucket's native close and
      // drift vs the anchor (same-bucket closes, the production formula).
      const ruler = quoteAnchors ? { anchors: quoteAnchors } : null;
      let quoteRows = null;
      for (const [u, q] of Object.entries(quoteIndicators)) {
        const v = q.getValueAt(param.time);
        if (!v || v.price == null || v.anchor == null || v.price <= 0) continue;
        const drift = candle
          ? ((candle.close / v.price) / v.anchor - 1) * 1e4 : null;
        quoteRows = quoteRows || {};
        quoteRows[u] = { native: v.price, anchor: v.anchor, drift };
      }

      // Exposure profile: nearest strike in the column at or before the
      // hovered time (the same target-row rule the plugin draws with).
      let expLevel = null;
      if (exposureRowsForTooltip && price != null) {
        let col = null;
        for (const c of exposureRowsForTooltip) {
          if (c.time <= param.time && (!col || c.time > col.time)) col = c;
        }
        if (!col) col = exposureRowsForTooltip[0];
        let best = null;
        for (const lv of (col?.levels || [])) {
          const d = Math.abs(lv.price - price);
          if (d <= 2.5 && (!best || d < Math.abs(best.price - price))) best = lv;
        }
        if (best) expLevel = { ...best, colTime: col.time };
      }

      tooltip.innerHTML = buildTooltipHtml({
        time: param.time, candle, price, zones, focusLevels, vwapAt, oiBar,
        ruler, quoteRows, expLevel,
      });
      tooltip.style.display = 'block';

      // Position: follow cursor, flip when near the right/bottom edge
      const margin = 14;
      const cw = containerRef.current.clientWidth;
      const ch = containerRef.current.clientHeight;
      const tw = tooltip.offsetWidth;
      const th = tooltip.offsetHeight;
      let left = param.point.x + margin;
      if (left + tw > cw - 8) left = param.point.x - tw - margin;
      let top = param.point.y + margin;
      if (top + th > ch - 8) top = Math.max(8, param.point.y - th - margin);
      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${top}px`;
    };
    chart.subscribeCrosshairMove(onCrosshairMove);

    const handleResize = () => {
      if (containerRef.current) {
        chart.applyOptions({ width: containerRef.current.clientWidth });
      }
    };
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      greeksCancelled = true;
      exposureCancelled = true;
      quotesCancelled = true;
      structureCancelled = true;
      chart.unsubscribeCrosshairMove(onCrosshairMove);
      priceLines.forEach(pl => series.removePriceLine(pl));
      if (greeksPlugin) { try { greeksPlugin.disable(); } catch (e) { /* chart disposed */ } }
      if (exposurePlugin) { try { exposurePlugin.disable(); } catch (e) { /* chart disposed */ } }
      for (const q of Object.values(quoteIndicators)) {
        try { q.disable(); } catch (e) { /* chart disposed */ }
      }
      if (vwapIndicator) { try { vwapIndicator.clear(); } catch (e) { /* chart disposed */ } }
      if (orderflowPlugin) { try { series.detachPrimitive(orderflowPlugin); } catch (e) { /* chart disposed */ } }
      chart.remove();
    };
  }, [data, height, JSON.stringify(plugins), JSON.stringify(highlight), JSON.stringify(range)]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div style={{
        border: '1px dashed #2a3344', borderRadius: '8px', padding: '20px',
        color: '#6b7280', fontSize: '13px', textAlign: 'center', margin: '16px 0',
      }}>
        {error}
      </div>
    );
  }

  return (
    <figure style={{ margin: '20px 0' }}>
      <div style={{
        border: '1px solid #2a3344',
        borderRadius: '8px',
        overflow: 'hidden',
        background: '#0f1419',
        position: 'relative',
      }}>
        <div ref={containerRef} style={{ height: `${height}px` }} />
        <div
          ref={tooltipRef}
          style={{
            display: 'none',
            position: 'absolute',
            zIndex: 20,
            pointerEvents: 'none',
            background: 'rgba(15, 20, 25, 0.96)',
            border: '1px solid #2a3344',
            borderRadius: '6px',
            padding: '8px 10px',
            fontSize: '11.5px',
            lineHeight: 1.55,
            minWidth: '190px',
            maxWidth: '340px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
          }}
        />
        {!data && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: '#4a5568', fontSize: '12px',
          }}>
            Loading example…
          </div>
        )}
        {data && (
          <div style={{
            position: 'absolute', top: '8px', left: '10px',
            fontSize: '10px', color: '#4a5568', pointerEvents: 'none',
          }}>
            ES · {data.date} · {data.timeframe} · real session data · hover for details
          </div>
        )}
      </div>
      {caption && (
        <figcaption style={{
          fontSize: '12px', color: '#6b7280', marginTop: '6px',
          lineHeight: 1.5, fontStyle: 'italic',
        }}>
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
